import { Component, EventEmitter, HostListener, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthenticatorMode, AuthenticatorType } from 'src/app/constants/authenticator-type';
import { SvgColor, SvgMatType } from 'src/app/constants/icons';
import { LoginMode } from 'src/app/constants/login-type';
import { MaskType } from 'src/app/constants/masks';
import { AgentPaths, BasePaths, MemberPaths } from 'src/app/constants/routes';
import { Agent } from 'src/app/models/agent';
import { Authenticator, AuthenticatorQR, AuthenticatorRequest, MultiFactorAuthenticator } from 'src/app/models/authenticator';
import { ListItem } from 'src/app/models/listItem';
import { MemberAuthorizationResult, MultiMemberSelectResult } from 'src/app/models/member';
import { AuthService } from 'src/app/services/auth.service';
import { FormValidationService } from 'src/app/services/form-validation.service';
import { HelperService } from 'src/app/services/helper.service';
import { MaskService } from 'src/app/services/mask.service';

@Component({
    selector: 'mwp-mfa',
    templateUrl: './mfa.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MFAComponent implements OnInit {
    @Input() multiFactorAuthenticator: MultiFactorAuthenticator;
    @Input() isConfigurationMode: boolean;
    @Input() isVerifyCodeMode: boolean;

    @Output() public emitMultiMemberSelectResult: EventEmitter<MultiMemberSelectResult> = new EventEmitter();
    @Output() public cancel: EventEmitter<any> = new EventEmitter;

    public maskType = MaskType;
    public svgType = SvgMatType;
    public svgColor = SvgColor;
    public authenticatorType = AuthenticatorType;
    public authenticatorMode = AuthenticatorMode;

    public authenticatorTypes: ListItem[];
    public mode: AuthenticatorMode;
    public selectedAuthenticatorQR: AuthenticatorQR;
    public selectedAuthenticatorType: AuthenticatorType
    public loginMode: LoginMode

    public isMember: boolean;
    public isEmailOnly: boolean;
    public configPhoneNumber: boolean;
    public configQR: boolean;
    public buttonEnabled: boolean;
    public buttonVerifyByEmailEnabled: boolean;

    public onBoardingForm: FormGroup;
    public selectionModeForm: FormGroup;
    public configForm: FormGroup;
    public verifyForm: FormGroup;

    public verifyMessage: string;
    public verifyMessageKey: string;

    private countdown: number = 45;
    private failureToEmail: number = 0;
    private interval: any;

    @HostListener('window:keydown.enter', ['$event']) onEnter(e: Event): void {
        if (e instanceof KeyboardEvent)
            e.preventDefault();

        switch (this.mode) {
            case AuthenticatorMode.Configuration:
                if (this.onBoardingForm.valid)
                    this.onbaordingProcess();
                break;

            case AuthenticatorMode.Onboarding:
                if (this.onBoardingForm.valid) {
                    if (this.configPhoneNumber)
                        this.setPhoneAndSendCode();
                    if (this.configQR)
                        this.setQRAndSendCode();
                }
                break;

            case AuthenticatorMode.Selection:
                if (this.selectionModeForm.valid)
                    this.setSelection();
                break;

            case AuthenticatorMode.Verification:
                if (this.verifyForm.valid)
                    this.verifyCode();
                break;
        }
        e.preventDefault();
    }

    constructor(
        private formBuilder: FormBuilder,
        private authService: AuthService,
        private router: Router,
        private helperService: HelperService,
        private maskService: MaskService
    ) {
    }

    ngOnInit() {
        this.mode = this.multiFactorAuthenticator.requireOnboarding ? AuthenticatorMode.Onboarding : AuthenticatorMode.Selection;
        this.isMember = this.multiFactorAuthenticator.isMember;

        this.configForm = this.formBuilder.group({
            phoneNumber: [null, new FormControl([null, [Validators.required, FormValidationService.validPhoneNumber(true)]])],
        });

        this.selectionModeForm = this.formBuilder.group({
            verificationType: [null, [Validators.required]]
        });

        this.verifyForm = this.formBuilder.group({
            verifyCode: [null, Validators.required]
        });

        if (this.mode == AuthenticatorMode.Onboarding) {
            this.onBoardingForm = this.formBuilder.group({
                verificationType: [null, [Validators.required]],
            });
            this.authenticatorTypes = [
                { text: `Text Message`, value: AuthenticatorType.S },
                { text: `Phone Call`, value: AuthenticatorType.P },
                { text: `Email - ${this.multiFactorAuthenticator.email}`, value: AuthenticatorType.E }
            ];

            if (this.isMember) {
                this.onBoardingForm.addControl('phoneNumber', new FormControl([null, [Validators.required, FormValidationService.validPhoneNumber(true)]]));
            } else {
                this.authenticatorTypes.push({ text: `Authenticator App`, value: AuthenticatorType.Q });
            }
        } else {
            if (!this.multiFactorAuthenticator.cellPhoneNumber &&
                !this.multiFactorAuthenticator.phoneNumber &&
                !this.multiFactorAuthenticator.isQRConfigured) {
                this.isEmailOnly = true;
            } else {
                this.authenticatorTypes = new Array<ListItem>();
                if (this.multiFactorAuthenticator.cellPhoneNumber)
                    this.authenticatorTypes.push({ text: `Text Message - ${this.multiFactorAuthenticator.cellPhoneNumber}`, value: AuthenticatorType.S });

                if (this.multiFactorAuthenticator.phoneNumber)
                    this.authenticatorTypes.push({ text: `Phone Call - ${this.multiFactorAuthenticator.phoneNumber}`, value: AuthenticatorType.P });

                this.authenticatorTypes.push({ text: `Email - ${this.multiFactorAuthenticator.email}`, value: AuthenticatorType.E });

                if (!this.multiFactorAuthenticator.isMember && this.multiFactorAuthenticator.isQRConfigured) {
                    this.authenticatorTypes.push({ text: `Authenticator App`, value: AuthenticatorType.Q });
                }
            }
        }
    }

    public onbaordingProcess(): void {
        const l_VerifyType = this.onBoardingForm.get('verificationType').value;
        if (l_VerifyType == AuthenticatorType.E) {
            const l_AuthenticatorRequest = new AuthenticatorRequest();
            this.selectedAuthenticatorType = this.helperService.getEnumByKey(AuthenticatorType, l_VerifyType);
            l_AuthenticatorRequest.verificationTypeValue = this.selectedAuthenticatorType;
            this.authService.mfaSendCode(l_AuthenticatorRequest).subscribe((response: Authenticator) => {
                if (response.isVerificationSent)
                    this.setVerifcation(l_VerifyType);
            });
        } else if (this.isMember) {
            const l_AuthenticatorRequest = new AuthenticatorRequest();
            l_AuthenticatorRequest.phoneNumber = this.maskService.removePhoneMask(this.onBoardingForm.get('phoneNumber').value);
            this.selectedAuthenticatorType = this.helperService.getEnumByKey(AuthenticatorType, this.onBoardingForm.get('verificationType').value);
            l_AuthenticatorRequest.verificationTypeValue = this.selectedAuthenticatorType;
            this.authService.mfaSetCellphoneSendCode(l_AuthenticatorRequest).subscribe(() => {
                this.setVerifcation(AuthenticatorType[this.selectedAuthenticatorType]);
            });
        } else {
            this.setConfigurationForm(this.onBoardingForm.get('verificationType').value);
        }
    }

    public setSelection(): void {
        let l_VerifyType = this.selectionModeForm.get('verificationType').value;

        if (this.isEmailOnly)
            l_VerifyType = AuthenticatorType.E;

        this.selectedAuthenticatorType = this.helperService.getEnumByKey(AuthenticatorType, l_VerifyType);
        let l_SendCode: boolean;
        switch (l_VerifyType) {
            case AuthenticatorType.S:
                l_SendCode = !!this.multiFactorAuthenticator.cellPhoneNumber;
                break;

            case AuthenticatorType.P:
                l_SendCode = !!this.multiFactorAuthenticator.phoneNumber;
                break;

            case AuthenticatorType.E:
                l_SendCode = true;
                break;

            case AuthenticatorType.Q:
                if (this.multiFactorAuthenticator.isQRConfigured) {
                    this.setVerifcation(l_VerifyType);
                    return;
                }
                break;
        }

        if (l_SendCode) {
            const l_AuthenticatorRequest: AuthenticatorRequest = new AuthenticatorRequest();
            l_AuthenticatorRequest.verificationTypeValue = this.selectedAuthenticatorType;
            this.authService.mfaSendCode(l_AuthenticatorRequest).subscribe((response: Authenticator) => {
                if (response.isVerificationSent)
                    this.setVerifcation(l_VerifyType);
            });
            this.startTimer();
        } else {
            this.setConfigurationForm(l_VerifyType);
        }

    }

    private setVerifcation(authType: AuthenticatorType): void {
        let l_VerifyMessage: string;
        let l_key: string = 'mfaVerify'
        switch (authType) {
            case AuthenticatorType.S:
                l_VerifyMessage = 'A code has been sent by Text Message to your phone.';
                l_key += AuthenticatorType.S.toString();
                break;

            case AuthenticatorType.P:
                l_VerifyMessage = 'You will receive a phone call shortly with your code.';
                l_key += AuthenticatorType.P.toString();
                break;

            case AuthenticatorType.E:
                l_VerifyMessage = 'A code has ben sent to your email.';
                l_key += AuthenticatorType.E.toString();
                break;

            case AuthenticatorType.Q:
                l_VerifyMessage = 'Open your Authenticator APP for your code.';
                l_key += AuthenticatorType.Q.toString();
                this.buttonVerifyByEmailEnabled = true;
                break;
        }
        this.verifyMessage = l_VerifyMessage;
        this.verifyMessageKey = l_key;
        this.mode = AuthenticatorMode.Verification;
    }

    public setConfigurationForm(authType: string) {
        const l_PhoneNumber = this.configForm.get('phoneNumber');

        this.configPhoneNumber =
            this.configQR = false;

        this.resetControl(l_PhoneNumber);
        switch (authType) {
            case AuthenticatorType.P:
                this.selectedAuthenticatorType = AuthenticatorType.P;
                this.configPhoneNumber = true;
                l_PhoneNumber.setValidators([Validators.required, FormValidationService.validPhoneNumber(true)]);
                l_PhoneNumber.updateValueAndValidity();
                this.mode = AuthenticatorMode.Configuration;
                break;

            case AuthenticatorType.S:
                this.selectedAuthenticatorType = AuthenticatorType.S;
                this.configPhoneNumber = true;
                l_PhoneNumber.setValidators([Validators.required, FormValidationService.validPhoneNumber(true)]);
                l_PhoneNumber.updateValueAndValidity();
                this.mode = AuthenticatorMode.Configuration;
                break;

            case AuthenticatorType.Q:
                this.selectedAuthenticatorType = AuthenticatorType.Q;
                this.configQR = true;
                this.authService.mfaGenerateQR().subscribe((response: AuthenticatorQR) => {
                    this.selectedAuthenticatorQR = response;
                    this.mode = AuthenticatorMode.Configuration;
                });
                break;
        }

        this.selectedAuthenticatorType = this.helperService.getEnumByKey(AuthenticatorType, authType);
    }

    private resetControl(control: AbstractControl): void {
        control.setValue(null);
        control.clearValidators();
        control.updateValueAndValidity();
    }

    public resetConfiugration() {
        this.mode = this.multiFactorAuthenticator.requireOnboarding ? AuthenticatorMode.Onboarding : AuthenticatorMode.Selection;
    }

    public cancelLogin() {
        this.cancel.emit()
    }

    public setPhoneAndSendCode(): void {
        const l_AuthenticatorRequest = new AuthenticatorRequest();
        l_AuthenticatorRequest.phoneNumber = this.maskService.removePhoneMask(this.configForm.get('phoneNumber').value);
        l_AuthenticatorRequest.verificationTypeValue = this.selectedAuthenticatorType;
        this.authService.mfaSetCellphoneSendCode(l_AuthenticatorRequest).subscribe(() => {
            this.startTimer();
            this.setVerifcation(AuthenticatorType[this.selectedAuthenticatorType]);
        });
    }

    public setQRAndSendCode(): void {
        this.authService.mfaSetQR(this.selectedAuthenticatorQR).subscribe(() => {
            this.setVerifcation(AuthenticatorType.Q);
        });
    }

    public verifyCode(): void {
        const l_AuthenticatorRequest = new AuthenticatorRequest();
        l_AuthenticatorRequest.verificationTypeValue = this.selectedAuthenticatorType;
        l_AuthenticatorRequest.verifyCode = this.verifyForm.get('verifyCode').value;
        if (this.isMember) {
            this.authService.mfaMemberVerifyLogin(l_AuthenticatorRequest).subscribe((response: MemberAuthorizationResult) => {
                if (response.isCodeVerfied) {
                    if (response.isMemberAutoLoggedIn) {
                        this.router.navigateByUrl(MemberPaths.Dashboard);
                    } else {
                        const l_MultiMemberAuthorization: MultiMemberSelectResult = new MultiMemberSelectResult();
                        l_MultiMemberAuthorization.members = response.members;
                        this.emitMultiMemberSelectResult.emit(l_MultiMemberAuthorization);
                    }
                } else {
                    if (!this.buttonVerifyByEmailEnabled && this.selectedAuthenticatorType != AuthenticatorType.E) {
                        this.failureToEmail++;
                        this.buttonVerifyByEmailEnabled = this.failureToEmail > 1;
                    }
                }
            });
        } else {
            this.authService.mfaAgentVerifyLogin(l_AuthenticatorRequest, this.multiFactorAuthenticator).subscribe((response: Agent) => {
                if (response.isCodeVerfied) {
                    if (response.mustResetPassword)
                        this.router.navigateByUrl(BasePaths.AgentPasswordExpired);
                    else
                        this.router.navigateByUrl(AgentPaths.CounselorMemberLookup);
                } else {
                    if (!this.buttonVerifyByEmailEnabled && this.selectedAuthenticatorType != AuthenticatorType.E) {
                        this.failureToEmail++;
                        this.buttonVerifyByEmailEnabled = this.failureToEmail > 1;
                    }
                }
            });
        }
    }

    public resendCode(): void {
        this.startTimer();
        const l_AuthenticatorRequest: AuthenticatorRequest = new AuthenticatorRequest();
        l_AuthenticatorRequest.verificationTypeValue = this.selectedAuthenticatorType;
        this.authService.mfaSendCode(l_AuthenticatorRequest).subscribe((response) => {
        });
    }

    public getResendButtonText(): string {
        if (this.buttonEnabled)
            return 'Resend Code';

        const minutes = Math.floor(this.countdown / 60);
        const seconds = this.countdown % 60;
        return `Resend Code in ${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    }

    public sendCodeByEmail(): void {
        this.buttonEnabled = false;
        this.selectedAuthenticatorType = this.helperService.getEnumByKey(AuthenticatorType, AuthenticatorType.E);
        const l_AuthenticatorRequest: AuthenticatorRequest = new AuthenticatorRequest();
        l_AuthenticatorRequest.verificationTypeValue = this.selectedAuthenticatorType;
        this.authService.mfaSendCode(l_AuthenticatorRequest).subscribe((response: Authenticator) => {
            if (response.isVerificationSent) {
                this.verifyForm.reset();
                this.buttonVerifyByEmailEnabled = false;
                this.setVerifcation(AuthenticatorType.E);
                this.startTimer();
            }
        });
    }

    private startTimer(): void {
        this.clearTimer();
        this.countdown = 45;
        this.buttonEnabled = false;

        this.interval = setInterval(() => {
            if (this.countdown > 0) {
                this.countdown--;
            } else {
                this.clearTimer();
                this.buttonEnabled = true;
            }
        }, 1000);
    }

    private clearTimer(): void {
        if (this.interval)
            clearInterval(this.interval);
    }
}